import { useState, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import { getInitials } from '@/utils/textFormat';
import { orgLogoBox, orgLogoImg, orgLogoFallback } from './orgLogo.css';

export interface OrgLogoProps {
  /** Logo image URL (served at a 3:1 aspect ratio). */
  src?: string | null;
  /** Org/network name — used for the alt text and the initials fallback. */
  name?: string | null;
  /**
   * Box height in px. The box renders 3:1 (width = height × 3) to match the
   * served logo aspect ratio.
   */
  height: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Displays an organization / network logo at its served 3:1 (banner) aspect
 * ratio with `object-fit: contain`, so wide logos are shown whole instead of
 * being square-cropped (the Avatar primitive forced 1:1 + `object-fit: cover`).
 * Falls back to the entity's initials when there is no logo or the image fails
 * to load. See uptake #4099.
 */
export function OrgLogo({ src, name, height, className, style }: OrgLogoProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const width = height * 3;
  // Scale the fallback initials with the box, with a sensible floor.
  const fontSize = Math.max(11, Math.round(height * 0.4));

  return (
    <div className={clsx(orgLogoBox, className)} style={{ width, height, ...style }}>
      {showImage ? (
        <img
          className={orgLogoImg}
          src={src as string}
          alt={name ? `${name} logo` : 'Organization logo'}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className={orgLogoFallback} style={{ fontSize }}>
          {getInitials(name)}
        </span>
      )}
    </div>
  );
}
